// Tabs overview page: every open table session at a glance.
//
// Each open tab is one card showing:
//   * Table number + time open
//   * Round count + running total
//   * Each round with its status, who placed it, and item count
//   * "Close tab" button (idempotent — closing twice is a no-op)
//
// Auto-refreshes every 30s in case other staff close tabs from
// elsewhere. Manual refresh available too.
import React, { useEffect, useRef, useState, useCallback } from "react";
import { API_URL, closeTab, fetchOpenTabs, getCurrency, getRole, hasFeature, fetchMyShifts, fetchTables, recordTabPayment, fetchPaymentSettings, savePaymentSettings, uploadPaymentQr, fetchInhouseGuestsLite, chargeTabToRoom, fetchOpenBlocksLite, chargeTabToGroup, getUser, refreshMe} from "../services/api";
import { formatPrice } from "../utils/currency";
import { parseServerDate } from "../utils/time";
import { useT } from "../i18n/useT";

// AM/OM are machine drafts (2026-06) — pending native review
const STRINGS = {
  en: {
    table: "Table",
    filter_all: "All areas",
    search_table: "Search table #",
    area_unassigned: "Unassigned",
    open_for: "open {d}",
    rounds_one: "{n} round",
    rounds_other: "{n} rounds",
    opened_by: "opened by {name}",
    running_total: "running total",
    items_one: "{n} item",
    items_other: "{n} items",
    by_name: "👋 by {name}",
    qr: "📱 QR",
    closing: "Closing…",
    close_tab: "✓ Close order",
    receipt: "Receipt",
    round_n: "Round {n}",
    receipt_title: "Receipt for round {n} (order #{id})",
    page_title: "Open orders",
    refresh: "Refresh",
    intro:
      "Every table currently seated. New rounds (QR or waiter) attach to the table's open order automatically. Close the order when the guest leaves so the next visit starts fresh.",
    loading: "Loading…",
    empty: "No open orders right now. New guest orders appear here automatically.",
    err_load: "Could not load tabs.",
    err_close: "Could not close tab.",
    confirm_close: "Close this order? The guest's check is final.",
    record_payment: "Record payment",
    pay_method: "Method",
    m_telebirr: "Telebirr",
    m_cbe: "CBE Birr",
    m_cash: "Cash",
    pay_reference: "Transaction / reference no.",
    pay_confirm: "Confirm payment",
    pay_busy: "Recording…",
    err_pay: "Could not record payment.",
    pay_hint: "Guest transferred and showed proof? Record it — this also closes the order.",
    pay_setup_title: "Payment details (shown to guests)",
    pay_setup_hint: "The Telebirr / CBE Birr number (and QR) guests transfer to.",
    pay_telebirr_no: "Telebirr number",
    pay_cbe_no: "CBE Birr number",
    pay_qr_url: "Payment QR image URL (optional)",
    pay_save: "Save",
    pay_saved: "Saved",
    show_qr: "Show payment",
    scan_to_pay: "Show the guest how to pay",
    pay_close: "Close",
    no_qr_set: "No payment QR set. Add one in Payment details.",
    svc_charge: "Service charge %",
    vat_l: "VAT %",
    incl_vat: "Menu prices already include VAT",
    sub_l: "Subtotal",
    service_l: "Service",
    vat_short: "VAT",
    up_qr: "Upload QR image",
    up_qr_busy: "Uploading…",
    charge_to_room: "Charge to room",
    search_room_or_name: "Search room # or guest name",
    charged_to_room: "Charged to room {room}", approval_requested: "Approval request sent to guest.",
    no_inhouse: "No in-house guests right now.",
    err_room: "Could not charge to room.",
    charge_confirm: "Send this bill to Room {room} · {guest}?  Amount: {amount}",
    charge_to_group: "Charge to group",
    search_group: "Search group / company",
    charged_to_group: "Charged to group {group}",
    no_blocks: "No open groups right now.",
    err_group: "Could not charge to group.",
    group_confirm: "Send this bill to group {group}?  Amount: {amount}",
    v_title: "Charge to room",
    v_not_receipt: "Not a payment receipt",
    v_room: "Room",
    v_guest: "Guest",
    v_tab: "Tab",
    v_total: "Total charged to room",
    v_sign: "Guest signature",
    v_settle: "Charged to the room account — settle at checkout.",
    print_for_signature: "Print for signature",
  },
  am: {
    table: "ጠረጴዛ",
    open_for: "ክፍት {d}",
    rounds_one: "{n} ዙር",
    rounds_other: "{n} ዙሮች",
    opened_by: "የተከፈተው በ{name}",
    running_total: "ድምር በሂደት",
    items_one: "{n} ዕቃ",
    items_other: "{n} ዕቃዎች",
    by_name: "👋 በ{name}",
    qr: "📱 QR",
    closing: "በመዝጋት ላይ…",
    close_tab: "✓ ትዕዛዝ ዝጋ",
    receipt: "ደረሰኝ",
    round_n: "ዙር {n}",
    receipt_title: "ለዙር {n} ደረሰኝ (ትዕዛዝ #{id})",
    page_title: "ክፍት ትዕዛዞች",
    refresh: "አድስ",
    intro:
      "በአሁኑ ጊዜ የተቀመጡ ሁሉም ጠረጴዛዎች። አዲስ ዙሮች (QR ወይም አስተናጋጅ) ራሳቸውን በጠረጴዛው ክፍት ትዕዛዝ ላይ ያያይዛሉ። ቀጣዩ ጉብኝት በአዲስ እንዲጀምር ደንበኛው ሲወጣ ትዕዛዙን ዝጋ።",
    loading: "በመጫን ላይ…",
    empty: "በአሁኑ ጊዜ ክፍት ትዕዛዝ የለም። አዲስ የደንበኛ ትዕዛዞች እዚህ ራሳቸውን ችለው ይታያሉ።",
    err_load: "ትዕዛዞችን መጫን አልተቻለም።",
    err_close: "ትዕዛዙን መዝጋት አልተቻለም።",
    confirm_close: "ይህን ትዕዛዝ ይዘጋ? የደንበኛው ሂሳብ የመጨረሻ ነው።",
    record_payment: "ክፍያ መዝግብ",
    pay_method: "ዘዴ",
    m_telebirr: "ቴሌብር",
    m_cbe: "ሲቢኢ ብር",
    m_cash: "ጥሬ ገንዘብ",
    pay_reference: "የግብይት / ማመሳከሪያ ቁጥር",
    pay_confirm: "ክፍያ አረጋግጥ",
    pay_busy: "በመመዝገብ ላይ…",
    err_pay: "ክፍያ መመዝገብ አልተቻለም።",
    pay_hint: "እንግዳው አስተላልፎ ማረጋገጫ አሳይቷል? እዚህ ይመዝግቡ — ይህ ትዕዛዙንም ይዘጋል።",
    pay_setup_title: "የክፍያ ዝርዝሮች (ለእንግዶች የሚታይ)",
    pay_setup_hint: "እንግዶች የሚያስተላልፉበት የቴሌብር / ሲቢኢ ብር ቁጥር (እና QR)።",
    pay_telebirr_no: "የቴሌብር ቁጥር",
    pay_cbe_no: "የሲቢኢ ብር ቁጥር",
    pay_qr_url: "የክፍያ QR ምስል URL (አማራጭ)",
    pay_save: "አስቀምጥ",
    pay_saved: "ተቀምጧል",
    show_qr: "ክፍያ አሳይ",
    scan_to_pay: "ለእንግዳው የክፍያ መንገድ አሳዩ",
    pay_close: "ዝጋ",
    no_qr_set: "የክፍያ QR አልተቀመጠም። በክፍያ ዝርዝሮች ውስጥ ያክሉ።",
    svc_charge: "የአገልግሎት ክፍያ %",
    vat_l: "ቫት %",
    incl_vat: "የምናሌ ዋጋዎች ቫት አካተዋል",
    sub_l: "ንዑስ ድምር",
    service_l: "አገልግሎት",
    vat_short: "ቫት",
    up_qr: "QR ምስል ስቀል",
    up_qr_busy: "በመስቀል ላይ…",
    charge_to_room: "ወደ ክፍል አስከፍል",
    search_room_or_name: "የክፍል # ወይም የእንግዳ ስም ይፈልጉ",
    charged_to_room: "ወደ ክፍል {room} ተከፍሏል", approval_requested: "የማጽደቅ ጥያቄ ለእንግዳው ተልኳል።",
    no_inhouse: "አሁን የቤት ውስጥ እንግዳ የለም።",
    err_room: "ወደ ክፍል ማስከፈል አልተቻለም።",
    charge_confirm: "ይህን ሂሳብ ወደ ክፍል {room} · {guest} ይላኩ?  መጠን: {amount}",
    charge_to_group: "ወደ ቡድን አስከፍል",
    search_group: "ቡድን / ድርጅት ይፈልጉ",
    charged_to_group: "ወደ ቡድን {group} ተከፍሏል",
    no_blocks: "አሁን ክፍት ቡድን የለም።",
    err_group: "ወደ ቡድን ማስከፈል አልተቻለም።",
    group_confirm: "ይህን ሂሳብ ወደ ቡድን {group} ይላኩ?  መጠን: {amount}",
    v_title: "ወደ ክፍል አስከፍል",
    v_not_receipt: "የክፍያ ደረሰኝ አይደለም",
    v_room: "ክፍል",
    v_guest: "እንግዳ",
    v_tab: "ትዕዛዝ",
    v_total: "ወደ ክፍል የተከፈለ ጠቅላላ",
    v_sign: "የእንግዳ ፊርማ",
    v_settle: "ወደ ክፍል ሂሳብ ተከፍሏል — ሲወጡ ያወራርዱ።",
    print_for_signature: "ለፊርማ አትም",
  },
  om: {
    table: "Minjaala",
    open_for: "banaa {d}",
    rounds_one: "marsaa {n}",
    rounds_other: "marsaa {n}",
    opened_by: "kan baname {name}n",
    running_total: "Ida'ama Adeemaa",
    items_one: "meeshaa {n}",
    items_other: "meeshaalee {n}",
    by_name: "👋 {name}n",
    qr: "📱 QR",
    closing: "Cufaa jira…",
    close_tab: "✓ Ajaja cufi",
    receipt: "Nagahee",
    round_n: "Marsaa {n}",
    receipt_title: "Nagahee marsaa {n} (ajaja #{id})",
    page_title: "Ajajoota Baname",
    refresh: "Haaromsi",
    intro:
      "Minjaalota amma tessisaman hunda. Marsaaleen haaraa (QR yookaan tajaajilaa) ofumaan ajaja minjaala sanaa banaa irratti maxxanu. Daawwannaan itti aanu haaraa akka eegaluuf yeroo maamilli ba'u ajaja cufi.",
    loading: "Fe'aat jira…",
    empty: "Amma ajajni banaan hin jiru. Ajajoonni maamilaa haaraan ofumaan asitti mul'atu.",
    err_load: "Ajajoota fe'uun hin danda'amne.",
    err_close: "Ajaja cufuun hin danda'amne.",
    confirm_close: "Ajaja kana cufuu? Herregni maamilaa kan dhumaati.",
    record_payment: "Kaffaltii galmeessi",
    pay_method: "Mala",
    m_telebirr: "Telebirr",
    m_cbe: "CBE Birr",
    m_cash: "Maallaqa",
    pay_reference: "Lakk. daddabarsaa / wabii",
    pay_confirm: "Kaffaltii mirkaneessi",
    pay_busy: "Galmeessaa jira…",
    err_pay: "Kaffaltii galmeessuun hin danda'amne.",
    pay_hint: "Maamilli dabarsee ragaa argisiise? Asitti galmeessi — kun ajajas ni cufa.",
    pay_setup_title: "Ragaa kaffaltii (maamiltootaaf mul'atu)",
    pay_setup_hint: "Lakkoofsa Telebirr / CBE Birr (fi QR) maamiltoonni itti dabarsan.",
    pay_telebirr_no: "Lakkoofsa Telebirr",
    pay_cbe_no: "Lakkoofsa CBE Birr",
    pay_qr_url: "URL suuraa QR kaffaltii (filannoo)",
    pay_save: "Olkaa'i",
    pay_saved: "Olkaa'ame",
    show_qr: "Kaffaltii agarsiisi",
    scan_to_pay: "Maamilaaf akkaata kaffaltii agarsiisi",
    pay_close: "Cufi",
    no_qr_set: "QR kaffaltii hin qindeeffamne. Ragaa kaffaltii keessatti dabali.",
    svc_charge: "Kaffaltii tajaajilaa %",
    vat_l: "VAT %",
    incl_vat: "Gatiiwwan menu VAT of keessaa qabu",
    sub_l: "Ida'ama xiqqaa",
    service_l: "Tajaajila",
    vat_short: "VAT",
    up_qr: "Suuraa QR ol kaa'i",
    up_qr_busy: "Ol kaa'aa jira…",
    charge_to_room: "Kutaatti kaffalchiisi",
    search_room_or_name: "Lakk. kutaa yookaan maqaa keessummaa barbaadi",
    charged_to_room: "Kutaa {room}tti kaffalame", approval_requested: "Gaaffiin mirkaneessaa keessummaadhaaf ergameera.",
    no_inhouse: "Yeroo ammaa keessummaan mana keessaa hin jiru.",
    err_room: "Kutaatti kaffalchiisuun hin danda'amne.",
    charge_confirm: "Herrega kana gara Kutaa {room} · {guest} ergi?  Hanga: {amount}",
    charge_to_group: "Gareetti kaffalchiisi",
    search_group: "Garee / dhaabbata barbaadi",
    charged_to_group: "Gareetti {group} kaffalame",
    no_blocks: "Yeroo ammaa gareen banaa hin jiru.",
    err_group: "Gareetti kaffalchiisuun hin danda'amne.",
    group_confirm: "Herrega kana gara garee {group} ergi?  Hanga: {amount}",
    v_title: "Kutaatti kaffalchiisi",
    v_not_receipt: "Nagahee kaffaltii miti",
    v_room: "Kutaa",
    v_guest: "Keessummaa",
    v_tab: "Ajaja",
    v_total: "Ida'ama kutaatti kaffalame",
    v_sign: "Mallattoo keessummaa",
    v_settle: "Herrega kutaatti kaffalame — yeroo ba'an kaffali.",
    print_for_signature: "Mallattoof maxxansi",
  },
};

function escapeHtml(s) {
  return String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}

function formatDuration(openedAt) {
  if (!openedAt) return "—";
  const ms = Date.now() - parseServerDate(openedAt).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins % 60}m`;
}

function statusPill(status) {
  const colors = {
    pending: { bg: "#fff8e7", fg: "#8a5a00" },
    preparing: { bg: "#e7f3ff", fg: "#004c80" },
    ready: { bg: "#e8f8e9", fg: "#1b6b1e" },
    served: { bg: "#f0f0f0", fg: "#333" },
    completed: { bg: "#f0f0f0", fg: "#666" },
    cancelled: { bg: "#fdecec", fg: "#a23a3a" },
  };
  const c = colors[status] || colors.pending;
  return (
    <span
      style={{
        background: c.bg,
        color: c.fg,
        padding: "1px 8px",
        borderRadius: 999,
        fontSize: "0.75rem",
        fontWeight: 600,
        textTransform: "uppercase",
      }}
    >
      {status}
    </span>
  );
}

function TabCard({ tab, onClose, closing, role, onRecordPayment, payInfo, inhouse, onLoadInhouse, onChargeToRoom, blocks, onLoadBlocks, onChargeToGroup, allowGroupCharge = true }) {
  // Sub-component reads currency directly — parent scope isn't shared.
  const currency = getCurrency();
  const apiBase = API_URL.replace(/\/$/, "");
  const canClose = role === "waiter" || role === "manager";
  const fdOn = hasFeature("front_desk");
  const { lang } = useT();
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;
  const payQr = payInfo?.payment_qr_url;
  const payTel = payInfo?.telebirr_number;
  const payCbe = payInfo?.cbe_birr_number;
  const hasPay = !!(payQr || payTel || payCbe);   // any payment detail configured
  const [payOpen, setPayOpen] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [method, setMethod] = useState("telebirr");
  const [reference, setReference] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const [roomOpen, setRoomOpen] = useState(false);
  const [gQuery, setGQuery] = useState("");
  const [groupOpen, setGroupOpen] = useState(false);
  const [bQuery, setBQuery] = useState("");
  const submitPay = async () => {
    setPayBusy(true);
    try { await onRecordPayment(tab.id, { method, reference }); }
    catch (e) { /* parent surfaces the error */ }
    finally { setPayBusy(false); }
  };
  const openRoom = () => { setRoomOpen((v) => !v); onLoadInhouse(); };
  const filteredGuests = () => {
    const s = gQuery.trim().toLowerCase();
    return (inhouse || []).filter((g) => !s
      || (g.room_name || "").toLowerCase().includes(s)
      || (g.guest_name || "").toLowerCase().includes(s));
  };
  const pickGuest = async (g) => {
    setPayBusy(true);
    try { await onChargeToRoom(tab, g); }
    catch (e) { /* parent surfaces the error */ }
    finally { setPayBusy(false); }
  };
  const openGroup = () => { setGroupOpen((v) => !v); onLoadBlocks(); };
  const filteredBlocks = () => {
    const s = bQuery.trim().toLowerCase();
    return (blocks || []).filter((b) => !s || (b.label || "").toLowerCase().includes(s));
  };
  const pickBlock = async (b) => {
    setPayBusy(true);
    try { await onChargeToGroup(tab, b); }
    catch (e) { /* parent surfaces the error */ }
    finally { setPayBusy(false); }
  };

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div>
          <h3 style={{ margin: 0 }}>
            {t("table")} {tab.table_number ?? "—"}
            <span
              className="muted"
              style={{ marginLeft: 10, fontSize: "0.85rem", fontWeight: 400 }}
            >
              · {t("open_for").replace("{d}", formatDuration(tab.opened_at))}
            </span>
          </h3>
          <div className="muted" style={{ fontSize: "0.85rem" }}>
            {(tab.round_count === 1 ? t("rounds_one") : t("rounds_other")).replace("{n}", tab.round_count)}
            {tab.opened_by_name && (
              <> · {t("opened_by").replace("{name}", tab.opened_by_name)}</>
            )}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "1.4rem", fontWeight: 700 }}>
            {formatPrice(tab.total || 0, currency)}
          </div>
          <div className="muted" style={{ fontSize: "0.75rem" }}>
            {t("running_total")}
          </div>
          {(tab.service_charge > 0 || tab.vat > 0) && (
            <div className="muted" style={{ fontSize: "0.7rem", marginTop: 2 }}>
              {t("sub_l")} {formatPrice(tab.subtotal || 0, currency)}
              {tab.service_charge > 0 && <> · {t("service_l")} {formatPrice(tab.service_charge, currency)}</>}
              {tab.vat > 0 && <> · {t("vat_short")} {formatPrice(tab.vat, currency)}</>}
            </div>
          )}
        </div>
      </div>

      {/* Rounds list */}
      <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
        {tab.rounds.map((r, i) => (
          <div
            key={r.order_id}
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr auto auto",
              gap: 8,
              alignItems: "center",
              padding: 8,
              background: "#fbf9f4",
              borderRadius: 8,
            }}
          >
            <div style={{ fontWeight: 700, minWidth: 28 }}>#{i + 1}</div>
            <div>
              <div style={{ fontSize: "0.9rem" }}>
                {(r.item_count === 1 ? t("items_one") : t("items_other")).replace("{n}", r.item_count)}
                {r.placed_by_name && (
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: "0.78rem",
                      color: "#8a5a00",
                    }}
                  >
                    {t("by_name").replace("{name}", r.placed_by_name)}
                  </span>
                )}
                {!r.placed_by_name && (
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: "0.78rem",
                      color: "#666",
                    }}
                  >
                    {t("qr")}
                  </span>
                )}
              </div>
              <div className="muted" style={{ fontSize: "0.75rem" }}>
                {parseServerDate(r.created_at).toLocaleTimeString()}
                {r.waiter_name && <> · 👤 {r.waiter_name}</>}
              </div>
            </div>
            <div>{statusPill(r.status)}</div>
            <div style={{ fontWeight: 600, textAlign: "right" }}>
              {formatPrice(r.subtotal || 0, currency)}
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div
        style={{
          marginTop: 12,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        {canClose && (
          <button
            type="button"
            className="btn"
            disabled={closing}
            onClick={() => onClose(tab.id)}
          >
            {closing ? t("closing") : t("close_tab")}
          </button>
        )}
        {canClose && (
          <button type="button" className="btn secondary" onClick={() => setPayOpen((v) => !v)}>
            💳 {t("record_payment")}
          </button>
        )}
        {canClose && hasPay && (
          <button type="button" className="btn secondary" onClick={() => setShowQR(true)}>
            📱 {t("show_qr")}
          </button>
        )}
        {canClose && fdOn && (
          <button type="button" className="btn secondary" onClick={openRoom}>
            🏨 {t("charge_to_room")}
          </button>
        )}
        {canClose && fdOn && allowGroupCharge && (
          <button type="button" className="btn secondary" onClick={openGroup}>
            👥 {t("charge_to_group")}
          </button>
        )}
        {/* Per-round receipts. One button per round so each link is
            accurate to its content. Friend-test feedback #78: the old
            single "First-round receipt" button was misleading when a
            tab had 3+ rounds because it always linked to round 1.
            Per-tab combined receipt is queued for V1.1. */}
        {tab.rounds.map((round, idx) => (
          <a
            key={round.order_id}
            className="btn secondary"
            href={`${apiBase}/public/receipt/${round.public_token}`}
            target="_blank"
            rel="noopener noreferrer"
            title={t("receipt_title").replace("{n}", idx + 1).replace("{id}", round.order_id)}
          >
            🧾 {tab.rounds.length === 1 ? t("receipt") : t("round_n").replace("{n}", idx + 1)}
          </a>
        ))}
      </div>

      {payOpen && canClose && (
        <div style={{ marginTop: 10, padding: 12, background: "#f2f9f8", borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: "#5f6b78", marginBottom: 8 }}>{t("pay_hint")}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("pay_method")}<br />
              <select value={method} onChange={(e) => setMethod(e.target.value)}>
                <option value="telebirr">{t("m_telebirr")}</option>
                <option value="cbe_birr">{t("m_cbe")}</option>
                <option value="cash">{t("m_cash")}</option>
              </select>
            </label>
            <label style={{ fontSize: 12, color: "#5f6b78", flex: 1, minWidth: 160 }}>{t("pay_reference")}<br />
              <input value={reference} onChange={(e) => setReference(e.target.value)} style={{ width: "100%" }} placeholder="TLB-…" />
            </label>
            <button type="button" className="btn" disabled={payBusy} onClick={submitPay}>
              {payBusy ? t("pay_busy") : t("pay_confirm")}
            </button>
          </div>
        </div>
      )}

      {roomOpen && canClose && fdOn && (
        <div style={{ marginTop: 10, padding: 12, background: "#f2f9f8", borderRadius: 8 }}>
          <input value={gQuery} onChange={(e) => setGQuery(e.target.value)} placeholder={t("search_room_or_name")} style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #d6dede" }} />
          <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
            {inhouse == null ? (
              <span className="muted" style={{ fontSize: 13 }}>…</span>
            ) : filteredGuests().length === 0 ? (
              <span className="muted" style={{ fontSize: 13 }}>{t("no_inhouse")}</span>
            ) : filteredGuests().map((g) => (
              <button key={g.id} type="button" className="btn secondary" style={{ textAlign: "left" }} disabled={payBusy} onClick={() => pickGuest(g)}>
                {g.room_name ? <b>{g.room_name}</b> : null}{g.room_name ? " · " : ""}{g.guest_name}
              </button>
            ))}
          </div>
        </div>
      )}

      {groupOpen && canClose && fdOn && (
        <div style={{ marginTop: 10, padding: 12, background: "#f2f9f8", borderRadius: 8 }}>
          <input value={bQuery} onChange={(e) => setBQuery(e.target.value)} placeholder={t("search_group")} style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #d6dede" }} />
          <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
            {blocks == null ? (
              <span className="muted" style={{ fontSize: 13 }}>…</span>
            ) : filteredBlocks().length === 0 ? (
              <span className="muted" style={{ fontSize: 13 }}>{t("no_blocks")}</span>
            ) : filteredBlocks().map((b) => (
              <button key={b.id} type="button" className="btn secondary" style={{ textAlign: "left" }} disabled={payBusy} onClick={() => pickBlock(b)}>
                {b.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {showQR && (
        <div onClick={() => setShowQR(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 20, textAlign: "center", maxWidth: 340 }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>{t("scan_to_pay")}</div>
            {payQr && (
              <img src={payQr} alt="Payment QR" style={{ width: 240, maxWidth: "100%", borderRadius: 8 }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
            )}
            {(payTel || payCbe) && (
              <div style={{ marginTop: 10, fontSize: 15 }}>
                {payTel && <div style={{ padding: "3px 0" }}>Telebirr: <b>{payTel}</b></div>}
                {payCbe && <div style={{ padding: "3px 0" }}>CBE Birr: <b>{payCbe}</b></div>}
              </div>
            )}
            <div style={{ fontWeight: 800, fontSize: "1.4rem", marginTop: 10, color: "#063b48" }}>{formatPrice(tab.total || 0, currency)}</div>
            <button type="button" className="btn secondary" style={{ marginTop: 14 }} onClick={() => setShowQR(false)}>{t("pay_close")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PaymentSetupCard({ t }) {
  const [s, setS] = useState({ telebirr_number: "", cbe_birr_number: "", payment_qr_url: "", service_charge_pct: "", vat_pct: "", prices_include_vat: false });
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    fetchPaymentSettings()
      .then((d) => setS({
        telebirr_number: d.telebirr_number || "",
        cbe_birr_number: d.cbe_birr_number || "",
        payment_qr_url: d.payment_qr_url || "",
        service_charge_pct: d.service_charge_pct ? String(d.service_charge_pct) : "",
        vat_pct: d.vat_pct ? String(d.vat_pct) : "",
        prices_include_vat: !!d.prices_include_vat,
      }))
      .catch(() => {});
  }, []);
  const save = async () => {
    setMsg("");
    try {
      await savePaymentSettings({
        ...s,
        service_charge_pct: Number(s.service_charge_pct) || 0,
        vat_pct: Number(s.vat_pct) || 0,
        prices_include_vat: !!s.prices_include_vat,
      });
      setMsg(t("pay_saved"));
    } catch (e) { /* ignore */ }
  };
  const onQrFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true); setMsg("");
    try {
      const d = await uploadPaymentQr(file);
      setS((prev) => ({ ...prev, payment_qr_url: d.payment_qr_url }));
      setMsg(t("pay_saved"));
    } catch (err) { /* ignore */ }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setOpen((o) => !o)}>
        <b>💳 {t("pay_setup_title")}</b>
        <span className="muted">{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>{t("pay_setup_hint")}</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("pay_telebirr_no")}<br /><input value={s.telebirr_number} onChange={(e) => setS({ ...s, telebirr_number: e.target.value })} style={{ width: "100%" }} /></label>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("pay_cbe_no")}<br /><input value={s.cbe_birr_number} onChange={(e) => setS({ ...s, cbe_birr_number: e.target.value })} style={{ width: "100%" }} /></label>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("pay_qr_url")}<br /><input value={s.payment_qr_url} onChange={(e) => setS({ ...s, payment_qr_url: e.target.value })} style={{ width: "100%" }} /></label>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("svc_charge")}<br /><input type="number" min="0" max="100" step="0.1" value={s.service_charge_pct} onChange={(e) => setS({ ...s, service_charge_pct: e.target.value })} style={{ width: "100%" }} /></label>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("vat_l")}<br /><input type="number" min="0" max="100" step="0.1" value={s.vat_pct} onChange={(e) => setS({ ...s, vat_pct: e.target.value })} style={{ width: "100%" }} /></label>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
            <label className="btn secondary small" style={{ cursor: "pointer", margin: 0 }}>
              {uploading ? t("up_qr_busy") : `⬆ ${t("up_qr")}`}
              <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={onQrFile} />
            </label>
            {s.payment_qr_url && (
              <img src={s.payment_qr_url} alt="QR" style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6, border: "1px solid #ddd" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
            )}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#5f6b78", marginTop: 10 }}>
            <input type="checkbox" checked={!!s.prices_include_vat} onChange={(e) => setS({ ...s, prices_include_vat: e.target.checked })} />
            {t("incl_vat")}
          </label>
          <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center" }}>
            <button type="button" className="btn small" onClick={save}>{t("pay_save")}</button>
            {msg && <span style={{ color: "#1D9E75", fontWeight: 600, fontSize: 13 }}>{msg}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TabsPage() {
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [areaByTable, setAreaByTable] = useState({});
  const [areaFilter, setAreaFilter] = useState("all");
  // Auto-default to the waiter's scheduled area for today (Hotel-tier
  // scheduling). Applied once, after tables load; manual changes win.
  const autoAreaRef = useRef(false);
  useEffect(() => {
    if (autoAreaRef.current) return;
    if (getRole() !== "waiter" || !hasFeature("scheduling")) return;
    const areaVals = new Set(Object.values(areaByTable).filter(Boolean));
    if (areaVals.size === 0) return;
    autoAreaRef.current = true;
    const today = new Date().toISOString().slice(0, 10);
    fetchMyShifts(today)
      .then((shifts) => {
        const a = ((shifts || []).find((s) => s.area) || {}).area;
        if (a && areaVals.has(a)) setAreaFilter(a);
      })
      .catch(() => {});
  }, [areaByTable]);
  const [tquery, setTquery] = useState("");
  const [pay, setPay] = useState(null);   // venue payment settings (incl. QR)
  const [inhouse, setInhouse] = useState(null);   // in-house guests for room billing
  const [blocks, setBlocks] = useState(null);     // open groups for group billing
  const [notice, setNotice] = useState("");
  const [signSlip, setSignSlip] = useState(null);  // {tab, guest} of the last room charge, for an optional signature slip
  const [allowGroupCharge, setAllowGroupCharge] = useState(true);   // per-hotel billing policy
  useEffect(() => { refreshMe().then((me) => setAllowGroupCharge(me.allow_group_charge !== false)).catch(() => {}); }, []);
  const role = getRole();
  const { lang } = useT();
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;

  const load = useCallback(async () => {
    try {
      const data = await fetchOpenTabs();
      setTabs(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || t("err_load"));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    fetchTables()
      .then((rows) => {
        const m = {};
        rows.forEach((r) => { if (r.table_number != null) m[r.table_number] = r.area || null; });
        setAreaByTable(m);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchPaymentSettings().then((d) => setPay(d)).catch(() => {});
  }, []);

  const handleClose = async (tabId) => {
    if (!window.confirm(t("confirm_close"))) return;
    setClosingId(tabId);
    try {
      await closeTab(tabId);
      // Remove from the open-tabs list immediately for snappy UX
      setTabs((cur) => cur.filter((tb) => tb.id !== tabId));
    } catch (e) {
      setError(e?.response?.data?.detail || t("err_close"));
    } finally {
      setClosingId(null);
    }
  };

  const handleRecordPayment = async (tabId, body) => {
    try {
      await recordTabPayment(tabId, body);
      setTabs((cur) => cur.filter((tb) => tb.id !== tabId)); // settled -> closed
    } catch (e) {
      setError(e?.response?.data?.detail || t("err_pay"));
      throw e;
    }
  };

  const loadInhouse = useCallback(() => {
    if (inhouse == null) fetchInhouseGuestsLite().then(setInhouse).catch(() => setInhouse([]));
  }, [inhouse]);

  const loadBlocks = useCallback(() => {
    if (blocks == null) fetchOpenBlocksLite().then(setBlocks).catch(() => setBlocks([]));
  }, [blocks]);

  const handleChargeToRoom = async (tab, guest) => {
    // Confirm before posting to the room folio. Vouchers now print at the
    // front desk (reception), NOT here — this only sends the charge.
    const msg = t("charge_confirm")
      .replace("{room}", guest.room_name || "—")
      .replace("{guest}", guest.guest_name || "—")
      .replace("{amount}", formatPrice(tab.total || 0, getCurrency()));
    if (!window.confirm(msg)) return;
    try {
      const res = await chargeTabToRoom(tab.id, guest.id);
      setTabs((cur) => cur.filter((tb) => tb.id !== tab.id)); // charged -> closed
      setError(null);
      setNotice(t("charged_to_room").replace("{room}", guest.room_name || guest.guest_name)
        + (res && res.approval_sent ? " " + t("approval_requested") : ""));
      // Offer an OPTIONAL signature slip (no auto-print).
      setSignSlip({ tab, guest });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError((d && typeof d === "object" ? d.message : d) || t("err_room"));
      throw e;
    }
  };

  const handleChargeToGroup = async (tab, blk) => {
    // Confirm before posting to the group master bill. Group charges print on
    // the group statement at the front desk — no per-charge voucher here.
    const msg = t("group_confirm")
      .replace("{group}", blk.label || "—")
      .replace("{amount}", formatPrice(tab.total || 0, getCurrency()));
    if (!window.confirm(msg)) return;
    try {
      await chargeTabToGroup(tab.id, blk.id);
      setTabs((cur) => cur.filter((tb) => tb.id !== tab.id)); // charged -> closed
      setError(null);
      setNotice(t("charged_to_group").replace("{group}", blk.label || ""));
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError((d && typeof d === "object" ? d.message : d) || t("err_group"));
      throw e;
    }
  };

  // Room-charge voucher — a CONTROL document the guest signs and the outlet
  // keeps. NOT a payment receipt. Browser print (window.open), thermal width,
  // mirroring the receipt styling. Amount is authoritative from the
  // charge-to-room response (net posted to the folio). Kept as the shared
  // reference markup — vouchers now print at the front desk (ReservationsPage);
  // staff may also print an optional signature slip via the button below.
  const printRoomVoucher = (tab, guest, res) => {
    const currency = getCurrency();
    const money = (v) => formatPrice(v || 0, currency);
    const items = (tab.rounds || []).flatMap((r) => r.items || []);
    const rows = items.map((it) => `<tr><td>${it.quantity}× ${escapeHtml(it.name)}</td><td class="r">${money(it.line_total)}</td></tr>`).join("");
    const hotel = res.hotel || (getUser() || {}).name || "";
    const room = res.room_name || guest.room_name || "";
    const gname = res.guest_name || guest.guest_name || "";
    const amount = res.amount != null ? res.amount : tab.subtotal;
    const tableLbl = tab.table_number != null ? ` · ${t("table")} ${tab.table_number}` : "";
    const w = window.open("", "_blank", "width=380,height=640");
    if (!w) return;
    w.document.write(`<html><head><meta charset="utf-8"><title>${escapeHtml(hotel)} — ${escapeHtml(t("v_title"))}</title><style>
      body{font-family:Arial,Helvetica,sans-serif;max-width:360px;margin:0 auto;padding:16px;color:#111}
      h2{margin:0;font-size:17px}.muted{color:#666;font-size:11.5px}
      .head{text-align:center}
      .title{text-align:center;font-weight:700;font-size:15px;letter-spacing:.6px;text-transform:uppercase;margin:8px 0 0}
      .sub{text-align:center;font-size:11px;color:#666;margin-bottom:2px}
      table{width:100%;border-collapse:collapse;margin-top:6px;font-size:12.5px}td{padding:3px 0;vertical-align:top}
      .r{text-align:right}.tot td{border-top:1px solid #999;font-weight:700;padding-top:6px}
      .hr{border-top:1px dashed #999;margin:8px 0}
      .sign{margin-top:22px;font-size:12.5px}
      .note{margin-top:14px;font-size:11px;text-align:center;color:#555}</style></head><body>
      <div class="head"><h2>${escapeHtml(hotel)}</h2></div>
      <div class="title">${escapeHtml(t("v_title"))}</div>
      <div class="sub">${escapeHtml(t("v_not_receipt"))}</div>
      <div class="muted head">${new Date().toLocaleString()}</div>
      <div class="hr"></div>
      <div class="muted"><b>${t("v_room")}:</b> ${escapeHtml(room || "—")}</div>
      <div class="muted"><b>${t("v_guest")}:</b> ${escapeHtml(gname || "—")}</div>
      <div class="muted">${t("v_tab")} #${tab.id}${tableLbl}</div>
      <div class="hr"></div>
      ${rows ? `<table>${rows}</table>` : ""}
      <table><tr class="tot"><td>${t("v_total")}</td><td class="r">${money(amount)}</td></tr></table>
      <div class="sign">${t("v_sign")}: ______________________</div>
      <div class="note">${escapeHtml(t("v_settle"))}</div>
    </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 250);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <h1 style={{ marginBottom: 0 }}>{t("page_title")}</h1>
        <button
          type="button"
          className="btn secondary small"
          onClick={() => {
            setLoading(true);
            load();
          }}
        >
          {t("refresh")}
        </button>
      </div>
      <p className="muted" style={{ marginTop: 4 }}>
        {t("intro")}
      </p>

      {role === "manager" && <PaymentSetupCard t={t} />}

      {error && <div className="error">{error}</div>}
      {notice && (
        <div className="card" style={{ padding: "8px 12px", background: "#e8f8e9", color: "#1b6b1e", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span>✓ {notice}</span>
          <span style={{ display: "flex", gap: 8 }}>
            {signSlip && (
              <button type="button" className="btn secondary small"
                onClick={() => printRoomVoucher(signSlip.tab, signSlip.guest,
                  { amount: signSlip.tab.total, room_name: signSlip.guest.room_name, guest_name: signSlip.guest.guest_name })}>
                🖨 {t("print_for_signature")}
              </button>
            )}
            <button type="button" className="btn secondary small" onClick={() => { setNotice(""); setSignSlip(null); }}>×</button>
          </span>
        </div>
      )}

      {loading && tabs.length === 0 ? (
        <p className="muted">{t("loading")}</p>
      ) : tabs.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            {t("empty")}
          </p>
        </div>
      ) : (
        (() => {
          const withArea = tabs.map((tab) => ({ tab, area: areaByTable[tab.table_number] || t("area_unassigned") }));
          const areas = Array.from(new Set(withArea.map((x) => x.area))).sort((a, b) => a.localeCompare(b));
          const showFilter = areas.length > 1;
          // Menu-tag style: tap an area pill -> show only those tables (flat list).
          const q = tquery.trim();
          const base = (showFilter && areaFilter !== "all")
            ? withArea.filter((x) => x.area === areaFilter)
            : withArea;
          const visible = q ? base.filter((x) => String(x.tab.table_number ?? "").includes(q)) : base;
          return (
            <>
              <input value={tquery} onChange={(e) => setTquery(e.target.value)} placeholder={t("search_table")} style={{ padding: "8px 10px", border: "1px solid #d3c8af", borderRadius: 8, marginBottom: 10, width: 180, maxWidth: "100%", display: "block" }} />
              {showFilter && (
                <div className="tabs" style={{ margin: "0 0 12px", flexWrap: "wrap" }}>
                  <div className={"tab " + (areaFilter === "all" ? "active" : "")} onClick={() => setAreaFilter("all")}>{t("filter_all")} ({withArea.length})</div>
                  {areas.map((a) => (
                    <div key={a} className={"tab " + (areaFilter === a ? "active" : "")} onClick={() => setAreaFilter(a)}>{a} ({withArea.filter((x) => x.area === a).length})</div>
                  ))}
                </div>
              )}
              {visible.map(({ tab }) => (
                <TabCard key={tab.id} tab={tab} role={role} closing={closingId === tab.id} onClose={handleClose} onRecordPayment={handleRecordPayment} payInfo={pay} inhouse={inhouse} onLoadInhouse={loadInhouse} onChargeToRoom={handleChargeToRoom} blocks={blocks} onLoadBlocks={loadBlocks} onChargeToGroup={handleChargeToGroup} allowGroupCharge={allowGroupCharge} />
              ))}
            </>
          );
        })()
      )}
    </>
  );
}
